<template>
  <div class="layout">
    <Sidebar @toggle-sidebar="toggleSidebar" />
    <div class="main-content" :class="{ 'sidebar-collapsed': isSidebarCollapsed }">
      <AnalyticsDashboard :title="'Analytics Dashboard'" :autoRefresh="false">
        <template #main="{ userData, courseData, revenueData, assessmentData, engagementData, isLoading, dateRange }">
          <div class="dashboard-section">
            <div class="section-header">
              <h2 class="section-title">Overview</h2>
              <span class="date-range-indicator">{{ dateRange.formattedStart }} to {{ dateRange.formattedEnd }}</span>
            </div>
            
            <div class="metrics-grid columns-4">
              <!-- Total Users -->
              <AnalyticsCard
                title="Total Users"
                subtitle="Registered accounts"
                :value="userData.total_users || 0"
                icon="👥"
                iconBackground="#ebf5ff"
                :trend="userData.user_growth_percentage"
                :isLoading="isLoading"
                badge="Users"
                badgeType="info"
              >
                <template #expanded>
                  <AnalyticsChart
                    chartType="line"
                    title="User Growth Trend"
                    :data="userData.user_growth || []"
                    valueKey="count"
                    labelKey="month"
                    :isLoading="isLoading"
                    :dataFormatter="(val) => val.toLocaleString()"
                  />
                </template>
              </AnalyticsCard>
              
              <!-- Active Users -->
              <AnalyticsCard
                title="Active Users"
                subtitle="Monthly active users"
                :value="userData.active_users || 0"
                icon="✨"
                iconBackground="#ecfdf5"
                :compareValue="engagementData.monthly_active_users || 0"
                compareLabel="vs Previous Month"
                :isLoading="isLoading"
              />
              
              <!-- Retention Rate -->
              <AnalyticsCard
                title="Retention Rate"
                subtitle="30-day retention"
                :value="engagementData.retention_rate || 0"
                suffix="%"
                icon="🔄"
                iconBackground="#eff6ff"
                :isLoading="isLoading"
                :badge="getRetentionBadge(engagementData.retention_rate)"
                :badgeType="getRetentionBadgeType(engagementData.retention_rate)"
              />
              
              <!-- Average Session -->
              <AnalyticsCard
                title="Avg. Session Time"
                subtitle="Time spent per session"
                :value="formatDuration(engagementData.average_session_duration || 0)"
                icon="⏱️"
                iconBackground="#fef2f2"
                :isLoading="isLoading"
                :formatter="() => formatDuration(engagementData.average_session_duration || 0)"
              />
            </div>
          </div>
          
          <!-- Engagement Metrics -->
          <div class="dashboard-section">
            <div class="section-header">
              <h2 class="section-title">User Engagement</h2>
            </div>
            
            <div class="metrics-grid columns-2">
              <!-- Daily Active Users Trend -->
              <AnalyticsChart
                chartType="line"
                title="Active Users Over Time"
                :data="engagementData.active_users_trend || []"
                valueKey="count"
                labelKey="date"
                :isLoading="isLoading"
              />
              
              <!-- Hourly Engagement -->
              <AnalyticsChart
                chartType="bar"
                title="Engagement by Hour"
                :data="engagementData.engagement_by_hour || []"
                valueKey="count"
                labelKey="hour"
                :isLoading="isLoading"
                :dataFormatter="formatHour"
              />
            </div>
            
            <!-- User Segments -->
            <div class="metrics-grid columns-2">
              <!-- Device Distribution -->
              <AnalyticsChart
                chartType="pie"
                title="Device Distribution"
                :data="engagementData.device_distribution || []"
                valueKey="count"
                labelKey="device"
                :isLoading="isLoading"
              />
              
              <!-- Geo Distribution -->
              <AnalyticsChart
                chartType="horizontal-bar"
                title="Top Countries"
                :data="engagementData.country_distribution || []"
                valueKey="count"
                labelKey="country"
                :isLoading="isLoading"
              />
            </div>
          </div>
          
          <!-- Revenue Metrics -->
          <div class="dashboard-section">
            <div class="section-header">
              <h2 class="section-title">Revenue Analytics</h2>
            </div>
            
            <div class="metrics-grid columns-3">
              <!-- Total Revenue -->
              <AnalyticsCard
                title="Total Revenue"
                subtitle="All time"
                :value="revenueData.total_revenue || 0"
                prefix="$"
                icon="💰"
                iconBackground="#f0fdf4"
                :isLoading="isLoading"
              />
              
              <!-- Monthly Revenue -->
              <AnalyticsCard
                title="Monthly Revenue"
                subtitle="Current month"
                :value="revenueData.monthly_revenue || 0"
                prefix="$"
                icon="📅"
                iconBackground="#fef3c7"
                :isLoading="isLoading"
                :compareValue="getLastMonthRevenue(revenueData.revenue_by_month)"
                compareLabel="vs Last Month"
              />
              
              <!-- ARPU -->
              <AnalyticsCard
                title="ARPU"
                subtitle="Average Revenue Per User"
                :value="calculateARPU(revenueData, userData) || 0"
                prefix="$"
                icon="👤"
                iconBackground="#fae8ff"
                :isLoading="isLoading"
              />
            </div>
            
            <!-- Revenue Chart -->
            <AnalyticsChart
              chartType="bar"
              title="Revenue by Month"
              :data="revenueData.revenue_by_month || []"
              valueKey="revenue"
              labelKey="month"
              valuePrefix="$"
              :isLoading="isLoading"
            />
          </div>
          
          <!-- Course Analytics -->
          <div class="dashboard-section">
            <div class="section-header">
              <h2 class="section-title">Course Insights</h2>
            </div>
            
            <div class="metrics-grid columns-2">
              <AnalyticsCard
                title="Total Courses"
                subtitle="All courses"
                :value="courseData.total_courses || 0"
                icon="📚"
                iconBackground="#dbeafe"
                :isLoading="isLoading"
              />
              
              <AnalyticsCard
                title="Course Completion Rate"
                subtitle="Average completion percentage"
                :value="courseData.completion_rate || 0"
                suffix="%"
                icon="🏆"
                iconBackground="#fef3c7"
                :isLoading="isLoading"
              />
            </div>
            
            <!-- Category Distribution -->
            <AnalyticsChart
              chartType="pie"
              title="Course Categories"
              :data="courseData.category_distribution || []"
              valueKey="count"
              labelKey="category_name"
              :isLoading="isLoading"
            />
            
            <!-- Top Courses Table -->
            <MetricsTable
              title="Top Performing Courses"
              :data="courseData.top_courses || []"
              :columns="courseColumns"
              :filters="courseFilters"
              :isLoading="isLoading"
              :defaultSort="{ key: 'enrollment_count', order: 'desc' }"
            >
              <template #row-actions="{ item }">
                <button class="view-button" @click="viewCourseDetails(item.id)">View</button>
              </template>
            </MetricsTable>
          </div>
          
          <!-- Assessment Analytics -->
          <div class="dashboard-section">
            <div class="section-header">
              <h2 class="section-title">Assessment Performance</h2>
            </div>
            
            <div class="metrics-grid columns-3">
              <AnalyticsCard
                title="Total Answers"
                subtitle="All assessments"
                :value="assessmentData.total_answers || 0"
                icon="📝"
                iconBackground="#e0e7ff"
                :isLoading="isLoading"
              />
              
              <AnalyticsCard
                title="Average Score"
                subtitle="All assessments"
                :value="assessmentData.average_score || 0"
                suffix="%"
                icon="📊"
                iconBackground="#fae8ff"
                :isLoading="isLoading"
              />
              
              <AnalyticsCard
                title="Pass Rate"
                subtitle="Success percentage"
                :value="assessmentData.pass_rate || 0"
                suffix="%"
                icon="✅"
                iconBackground="#dcfce7"
                :isLoading="isLoading"
              />
            </div>
            
            <!-- Question Performance -->
            <MetricsTable
              title="Question Performance"
              :data="assessmentData.assessment_performance || []"
              :columns="questionColumns"
              :isLoading="isLoading"
              :defaultSort="{ key: 'difficulty_score', order: 'desc' }"
            />
          </div>
        </template>
      </AnalyticsDashboard>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';
import Sidebar from '@/components/Sidebar.vue';
import AnalyticsDashboard from '@/components/analytics/AnalyticsDashboard.vue';
import AnalyticsCard from '@/components/analytics/AnalyticsCard.vue';
import AnalyticsChart from '@/components/analytics/AnalyticsChart.vue';
import MetricsTable from '@/components/analytics/MetricsTable.vue';

export default {
  name: 'AnalyticsView',
  components: {
    Sidebar,
    AnalyticsDashboard,
    AnalyticsCard,
    AnalyticsChart,
    MetricsTable
  },
  setup() {
    const isSidebarCollapsed = ref(false);
    
    // Table columns and filters
    const courseColumns = [
      { key: 'title', label: 'Course Title', sortable: true },
      { key: 'category_name', label: 'Category', sortable: true },
      { key: 'enrollment_count', label: 'Enrollments', sortable: true, numeric: true },
      { key: 'completion_rate', label: 'Completion', sortable: true, numeric: true, suffix: '%' },
      { key: 'average_rating', label: 'Rating', sortable: true, numeric: true },
      { key: 'revenue', label: 'Revenue', sortable: true, numeric: true, prefix: '$' }
    ];
    
    const courseFilters = [
      {
        key: 'status',
        label: 'Status',
        options: [
          { value: 'active', label: 'Active' },
          { value: 'draft', label: 'Draft' },
          { value: 'archived', label: 'Archived' }
        ]
      }
    ];
    
    const questionColumns = [
      { key: 'question_id', label: 'Question ID', sortable: true },
      { key: 'total_attempts', label: 'Attempts', sortable: true, numeric: true },
      { key: 'correct_percentage', label: 'Correct %', sortable: true, numeric: true, suffix: '%' },
      { key: 'avg_time_seconds', label: 'Avg. Time', sortable: true, numeric: true, 
        formatter: (value) => `${Math.floor(value / 60)}m ${value % 60}s` },
      { key: 'difficulty_score', label: 'Difficulty', sortable: true, numeric: true,
        formatter: (value) => ['Easy', 'Medium', 'Hard'][Math.floor(value / 33.4)] || 'Unknown' }
    ];
    
    // Helper functions
    const formatDuration = (minutes) => {
      const mins = parseFloat(minutes);
      if (mins >= 60) {
        const hours = Math.floor(mins / 60);
        const remainingMins = Math.round(mins % 60);
        return `${hours}h ${remainingMins}m`;
      }
      return `${Math.round(mins)}m`;
    };
    
    const formatHour = (hour) => {
      if (hour === null) return 'N/A';
      const hourNum = parseInt(hour);
      return new Date(2000, 0, 1, hourNum).toLocaleString('en-US', {
        hour: 'numeric',
        hour12: true
      });
    };
    
    const getRetentionBadge = (value) => {
      if (!value) return '';
      if (value >= 70) return 'Excellent';
      if (value >= 50) return 'Good';
      if (value >= 30) return 'Average';
      return 'Poor';
    };
    
    const getRetentionBadgeType = (value) => {
      if (!value) return 'neutral';
      if (value >= 70) return 'success';
      if (value >= 50) return 'info';
      if (value >= 30) return 'warning';
      return 'danger';
    };
    
    const getLastMonthRevenue = (revenueByMonth) => {
      if (!revenueByMonth || revenueByMonth.length < 2) return 0;
      return revenueByMonth[revenueByMonth.length - 2].revenue || 0;
    };
    
    const calculateARPU = (revenueData, userData) => {
      if (!revenueData || !userData || !revenueData.monthly_revenue || !userData.active_users) {
        return 0;
      }
      
      const arpu = parseFloat(revenueData.monthly_revenue) / userData.active_users;
      return arpu.toFixed(2);
    };
    
    const viewCourseDetails = (courseId) => {
      // Implement course details navigation
      console.log(`View course details for: ${courseId}`);
    };
    
    const toggleSidebar = () => {
      isSidebarCollapsed.value = !isSidebarCollapsed.value;
    };
    
    return {
      isSidebarCollapsed,
      toggleSidebar,
      courseColumns,
      courseFilters,
      questionColumns,
      formatDuration,
      formatHour,
      getRetentionBadge,
      getRetentionBadgeType,
      getLastMonthRevenue,
      calculateARPU,
      viewCourseDetails
    };
  }
};
</script>

<style scoped>
.layout {
  display: flex;
  min-height: 100vh;
}

.main-content {
  flex: 1;
  margin-left: 280px;
  transition: margin-left 0.3s ease;
  background-color: #f8fafc;
}

.main-content.sidebar-collapsed {
  margin-left: 0;
}

.dashboard-section {
  margin-bottom: 2rem;
  background-color: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.section-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
}

.date-range-indicator {
  font-size: 0.875rem;
  color: #64748b;
  background-color: #f1f5f9;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
}

.metrics-grid {
  display: grid;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.columns-2 {
  grid-template-columns: repeat(2, 1fr);
}

.columns-3 {
  grid-template-columns: repeat(3, 1fr);
}

.columns-4 {
  grid-template-columns: repeat(4, 1fr);
}

.view-button {
  padding: 0.25rem 0.75rem;
  background-color: #3b82f6;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 0.75rem;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.view-button:hover {
  background-color: #2563eb;
}

@media (max-width: 1200px) {
  .columns-4 {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .columns-3 {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
  }
  
  .metrics-grid {
    grid-template-columns: 1fr;
  }
  
  .columns-2, 
  .columns-3, 
  .columns-4 {
    grid-template-columns: 1fr;
  }
  
  .dashboard-section {
    padding: 1rem;
  }
  
  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
}
</style> 